import React, { useEffect, useRef } from 'react';
import { useProtectedImage } from '../../hooks/useProtectedImage';
import LoadingSpinner from '../globals/LoadingSpinner';

/**
 * Composant pour afficher les images protégées
 * 
 * Protections:
 * - Image servie en base64 (pas de fichier téléchargeable)
 * - Clic-droit désactivé
 * - Drag-and-drop désactivé
 * - Watermark appliqué côté serveur
 * - Impossible de télécharger via l'URL directe
 */
export const ProtectedImage = ({
  imagePath,
  alt = 'Image protégée',
  className = '',
  objectFit = 'cover',
  showWatermark = true,
  renderWatermark = null,
  hoverPreview = false,
  hoverPreviewScale = 1.18,
  hoverPreviewLabel = 'Aperçu protégé'
}) => {
  const { image, loading, error } = useProtectedImage(imagePath);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);

  // Ajouter des protections supplémentaires
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Empêcher la copie de l'image
  const handleCopy = (e) => {
    e.preventDefault();
  };

  const drawImageToCanvas = (canvas, sourceImage, fitMode, scale = 1) => {
    if (!canvas || !sourceImage) return;

    const parent = canvas.parentElement;
    const targetWidth = parent?.clientWidth || canvas.clientWidth || sourceImage.naturalWidth;
    const targetHeight = parent?.clientHeight || canvas.clientHeight || sourceImage.naturalHeight;

    if (!targetWidth || !targetHeight) return;

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.clearRect(0, 0, targetWidth, targetHeight);

    const imageRatio = sourceImage.naturalWidth / sourceImage.naturalHeight;
    const canvasRatio = targetWidth / targetHeight;

    let drawWidth = targetWidth;
    let drawHeight = targetHeight;

    if (fitMode === 'contain') {
      if (imageRatio > canvasRatio) {
        drawHeight = targetWidth / imageRatio;
      } else {
        drawWidth = targetHeight * imageRatio;
      }
    } else {
      if (imageRatio > canvasRatio) {
        drawWidth = targetHeight * imageRatio;
      } else {
        drawHeight = targetWidth / imageRatio;
      }
    }

    drawWidth *= scale;
    drawHeight *= scale;

    const offsetX = (targetWidth - drawWidth) / 2;
    const offsetY = (targetHeight - drawHeight) / 2;

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(sourceImage, offsetX, offsetY, drawWidth, drawHeight);
  };

  useEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) return;

    containerElement.addEventListener('contextmenu', handleContextMenu);
    containerElement.addEventListener('dragstart', handleDragStart);
    containerElement.addEventListener('copy', handleCopy);

    return () => {
      containerElement?.removeEventListener('contextmenu', handleContextMenu);
      containerElement?.removeEventListener('dragstart', handleDragStart);
      containerElement?.removeEventListener('copy', handleCopy);
    };
  }, []);

  useEffect(() => {
    if (!image) return undefined;

    const sourceImage = new Image();
    sourceImage.decoding = 'async';
    sourceImage.src = image;

    const render = () => {
      drawImageToCanvas(canvasRef.current, sourceImage, objectFit, 1);

      if (hoverPreview) {
        drawImageToCanvas(previewCanvasRef.current, sourceImage, objectFit, hoverPreviewScale);
      }
    };

    sourceImage.onload = render;

    const observer = new ResizeObserver(() => {
      if (sourceImage.complete) {
        render();
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
      sourceImage.onload = null;
    };
  }, [image, objectFit, hoverPreview, hoverPreviewScale]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-200 ${className}`}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-red-100 text-red-600 ${className}`}>
        <div className="text-center">
          <p className="font-semibold">Impossible de charger l'image</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const defaultWatermark = () => {
    const rows = Array.from({ length: 18 }, (_, index) => index);
    const repeatedText = Array.from({ length: 7 }, () => 'KRAYNE AGENCY').join('   ');
    
    return (
      <div className="absolute inset-[-18%] overflow-hidden">
        {rows.map((row) => {
          const opacity = row % 2 === 0 ? 0.28 : 0.18;

          return (
            <div
              key={`watermark-row-${row}`}
              className="absolute left-1/2 w-[190%] -translate-x-1/2 text-[10px] sm:text-xs md:text-sm lg:text-base font-extrabold uppercase select-none whitespace-nowrap"
              style={{
                top: `${row * 7}%`,
                transform: `rotate(-28deg) translateY(${row % 2 === 0 ? '-6px' : '6px'})`,
                color: `rgba(255, 255, 255, ${opacity})`,
                textShadow: '0 0 10px rgba(0, 0, 0, 0.9), 1px 1px 2px rgba(0, 0, 0, 0.72)',
                letterSpacing: '0.14em',
                pointerEvents: 'none',
                WebkitTextStroke: '0.3px rgba(0, 0, 0, 0.32)',
              }}
            >
              {repeatedText}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`group/image-preview relative overflow-hidden ${className}`}
      onContextMenu={handleContextMenu}
      role="img"
      aria-label={alt}
    >
      <canvas
        ref={canvasRef}
        className="h-full w-full select-none pointer-events-none"
        aria-hidden="true"
      />
      {hoverPreview && (
        <div className="pointer-events-none absolute inset-0 z-20 opacity-0 transition-opacity duration-300 group-hover/image-preview:opacity-100">
          <canvas
            ref={previewCanvasRef}
            className="h-full w-full select-none"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10" />
          <div className="absolute left-3 top-3 rounded-full bg-black/65 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white shadow-xl">
            {hoverPreviewLabel}
          </div>
        </div>
      )}
      {showWatermark && (
        <div className="absolute inset-0 pointer-events-none">
          {renderWatermark ? renderWatermark() : defaultWatermark()}
        </div>
      )}
      {/* Protection anti-inspection */}
      <div 
        className="absolute inset-0 pointer-events-none" 
        style={{ 
          background: 'transparent',
          zIndex: 1
        }}
      />
    </div>
  );
};

export default ProtectedImage;
